import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClients, useCreateQuote, useAiEstimate, useUploadQuoteImages, useCreateClient, useKboLookup, useQuotaStatus } from '@/hooks/useApi';
import DescriptionAutocomplete from '@/components/DescriptionAutocomplete';
import QuotaExceededBanner from '@/components/QuotaExceededBanner';
import { getApiErrorCode, getApiErrorMessage } from '@/lib/errorCodes';
import type { AiEstimateItem } from '@/hooks/useApi';
import type { KboEnterprise } from '@/types';
import toast from 'react-hot-toast';

const DRAFT_STORAGE_KEY = 'quote_create_draft';

interface DraftState {
  clientId: string;
  clientLanguage: string;
  issueDate: string;
  validUntil: string;
  lines: LineItem[];
  notes: string;
  aiDescription: string;
  aiResults: AiEstimateItem[] | null;
  aiNote: string;
  showAiPanel: boolean;
  savedAt: number;
}

function loadDraft(): DraftState | null {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DraftState;
  } catch {
    return null;
  }
}

function saveDraft(state: DraftState) {
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
  } catch { /* quota exceeded — ignore */ }
}

function clearDraft() {
  localStorage.removeItem(DRAFT_STORAGE_KEY);
}

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'es', label: 'Español' },
];

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: string;
  vatRate: string;
}

const VAT_RATES = ['21', '12', '6', '0'];

const CATEGORY_COLORS: Record<string, string> = {
  materials: 'bg-blue-100 text-blue-800',
  labor: 'bg-amber-100 text-amber-800',
  consumables: 'bg-green-100 text-green-800',
  equipment: 'bg-purple-100 text-purple-800',
  subcontracting: 'bg-red-100 text-red-800',
};

/** Normalize price string: accept comma or point as decimal separator */
function normalizePrice(value: string): string {
  // Allow digits, single dot/comma, and leading minus
  let v = value.replace(/[^0-9.,\-]/g, '');
  // Replace comma with dot for internal use
  v = v.replace(',', '.');
  // Prevent multiple dots
  const parts = v.split('.');
  if (parts.length > 2) {
    v = parts[0] + '.' + parts.slice(1).join('');
  }
  return v;
}

/** Parse a price string (comma or point) to a number */
function parsePrice(value: string): number {
  const n = parseFloat(value.replace(',', '.'));
  return isNaN(n) ? 0 : n;
}

/** Coerce an AI-provided value (number or comma-formatted string) to a safe number */
function toSafeNumber(value: number | string): number {
  if (typeof value === 'number') return isNaN(value) ? 0 : value;
  return parsePrice(value);
}

export default function QuoteCreatePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: clientsData } = useClients({});
  const createQuote = useCreateQuote();
  const aiEstimate = useAiEstimate();
  const quota = useQuotaStatus();

  const uploadImages = useUploadQuoteImages();

  const draft = useMemo(() => loadDraft(), []);
  const queryClientId = searchParams.get('clientId');

  // Initialize clientId from draft or URL parameter
  const [clientId, setClientId] = useState(() => draft?.clientId || '');
  
  // URL clientId should override any restored draft client when explicitly provided.
  useEffect(() => {
    if (queryClientId) {
      setClientId((currentClientId) => (currentClientId === queryClientId ? currentClientId : queryClientId));
    }
  }, [queryClientId]);

  const [clientLanguage, setClientLanguage] = useState(draft?.clientLanguage ?? 'en');
  const [issueDate, setIssueDate] = useState(draft?.issueDate ?? new Date().toISOString().slice(0, 10));
  const [validUntil, setValidUntil] = useState(
    draft?.validUntil ?? new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)
  );
  const [lines, setLines] = useState<LineItem[]>(
    draft?.lines ?? [{ description: '', quantity: 1, unitPrice: '', vatRate: '21' }]
  );
  const [notes, setNotes] = useState(draft?.notes ?? '');

  // Quick-create client modal
  const createClient = useCreateClient();
  const [showClientModal, setShowClientModal] = useState(false);
  const emptyClientForm = { name: '', email: '', vatNumber: '', type: 'company', country: 'BE', address: '', city: '', postalCode: '', phone: '' };
  const [clientForm, setClientForm] = useState(emptyClientForm);
  const [vatLookup, setVatLookup] = useState('');
  const { data: kboData, isFetching: kboLoading } = useKboLookup(vatLookup);

  function applyKboData(kbo: KboEnterprise) {
    const street = [kbo.streetFr || kbo.streetNl, kbo.houseNumber, kbo.box].filter(Boolean).join(' ');
    setClientForm({
      ...clientForm,
      name: kbo.nameFr || kbo.nameNl || kbo.commercialName || clientForm.name,
      vatNumber: kbo.vatNumber,
      email: kbo.email || clientForm.email,
      phone: kbo.phone || clientForm.phone,
      address: street || clientForm.address,
      city: kbo.municipalityFr || kbo.municipalityNl || clientForm.city,
      postalCode: kbo.zipcode || clientForm.postalCode,
      country: 'BE',
      type: kbo.typeOfEnterprise === '1' ? 'individual' : 'company',
    });
    toast.success(t('kbo.dataApplied'));
  }

  function handleQuickCreateClient(e: React.FormEvent) {
    e.preventDefault();
    createClient.mutate(clientForm, {
      onSuccess: (newClient) => {
        setClientId(newClient.id);
        if (newClient.defaultLanguage) setClientLanguage(newClient.defaultLanguage);
        setShowClientModal(false);
        setClientForm(emptyClientForm);
        toast.success(t('clients.created'));
      },
      onError: (error) => {
        const errorCode = getApiErrorCode(error);
        if (errorCode) {
          const localized = t(errorCode);
          if (localized !== errorCode) {
            toast.error(localized);
            return;
          }
        }
        toast.error(t('common.error'));
      },
    });
  }

  // Image upload state
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Auto-set client language when selecting a client
  useEffect(() => {
    if (clientId && clientsData?.data) {
      const selected = clientsData.data.find((c) => c.id === clientId);
      if (selected?.defaultLanguage) {
        setClientLanguage(selected.defaultLanguage);
      }
    }
  }, [clientId, clientsData]);

  function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setImages((prev) => [...prev, ...files]);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
    if (imageInputRef.current) imageInputRef.current.value = '';
  }

  function removeImage(idx: number) {
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  }

  // AI estimation state
  const [showAiPanel, setShowAiPanel] = useState(draft?.showAiPanel ?? false);
  const [aiDescription, setAiDescription] = useState(draft?.aiDescription ?? '');
  const [aiResults, setAiResults] = useState<AiEstimateItem[] | null>(draft?.aiResults ?? null);
  const [aiNote, setAiNote] = useState<string>(draft?.aiNote ?? '');

  function handleResetAll() {
    if (!window.confirm(t('quotes.resetConfirm'))) return;
    setClientId('');
    setClientLanguage('en');
    setIssueDate(new Date().toISOString().slice(0, 10));
    setValidUntil(new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
    setLines([{ description: '', quantity: 1, unitPrice: '', vatRate: '21' }]);
    setNotes('');
    setAiDescription('');
    setAiResults(null);
    setAiNote('');
    setShowAiPanel(false);
    setImages([]);
    setImagePreviews([]);
    clearDraft();
  }

  // Speech-to-text dictation
  const SPEECH_LANG_MAP: Record<string, string> = { fr: 'fr-FR', nl: 'nl-BE', en: 'en-US', es: 'es-ES' };
  const [isListening, setIsListening] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  const toggleDictation = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error(t('quotes.ai.dictateUnsupported'));
      return;
    }

    if (isListening) {
      stopListening();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = SPEECH_LANG_MAP[i18n.language] || i18n.language;

    recognition.onresult = (event: { resultIndex: number; results: SpeechRecognitionResultList }) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          const transcript = event.results[i][0].transcript;
          setAiDescription((prev) => {
            const separator = prev && !prev.endsWith(' ') ? ' ' : '';
            return prev + separator + transcript;
          });
        }
      }
    };

    recognition.onerror = (event) => {
      if (event.error !== 'aborted') {
        toast.error(t('quotes.ai.dictateFailed'));
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
    toast.success(t('quotes.ai.dictateStarted'));
  }, [isListening, stopListening, t, i18n.language]);

  // Cleanup recognition on unmount
  useEffect(() => {
    return () => { recognitionRef.current?.stop(); };
  }, []);

  // Auto-save draft to localStorage (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      saveDraft({
        clientId,
        clientLanguage,
        issueDate,
        validUntil,
        lines,
        notes,
        aiDescription,
        aiResults,
        aiNote,
        showAiPanel,
        savedAt: Date.now(),
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [clientId, clientLanguage, issueDate, validUntil, lines, notes, aiDescription, aiResults, aiNote, showAiPanel]);

  // Show draft restored toast on mount
  const draftToastShown = useRef(false);

  useEffect(() => {
    if (draft && !draftToastShown.current) {
      draftToastShown.current = true;
      toast.success(t('quotes.draftRestored'), { id: 'quote-draft-restored' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addLine() {
    setLines([...lines, { description: '', quantity: 1, unitPrice: '1', vatRate: '21' }]);
  }

  function removeLine(idx: number) {
    if (lines.length <= 1) return;
    if (!window.confirm(t('quotes.confirmDeleteLine'))) return;
    setLines(lines.filter((_, i) => i !== idx));
  }

  function updateLine(idx: number, field: keyof LineItem, value: string | number) {
    setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  }

  function handlePriceChange(idx: number, raw: string) {
    updateLine(idx, 'unitPrice', normalizePrice(raw));
  }

  function handlePriceBlur(idx: number) {
    const val = parsePrice(lines[idx].unitPrice);
    if (val > 0) {
      updateLine(idx, 'unitPrice', val.toFixed(2));
    }
  }

  function lineTotal(l: LineItem) {
    const net = l.quantity * parsePrice(l.unitPrice);
    return { net, vat: net * (Number(l.vatRate) / 100), gross: net * (1 + Number(l.vatRate) / 100) };
  }

  const totals = lines.reduce((a, l) => { const lt = lineTotal(l); return { net: a.net + lt.net, vat: a.vat + lt.vat, gross: a.gross + lt.gross }; }, { net: 0, vat: 0, gross: 0 });

  /** Build existing lines context for AI so it avoids duplicates */
  function getExistingLinesForAi(): Array<{ description: string; quantity: number; unitPrice: number }> {
    return lines
      .filter((l) => l.description.trim() !== '')
      .map((l) => ({
        description: l.description,
        quantity: l.quantity,
        unitPrice: parsePrice(l.unitPrice),
      }));
  }

  function handleAiEstimate() {
    if (!aiDescription.trim()) {
      toast.error(t('quotes.ai.descriptionRequired'));
      return;
    }
    const existingLines = getExistingLinesForAi();
    aiEstimate.mutate({ description: aiDescription, existingLines }, {
      onSuccess: ({ items, note }) => {
        setAiResults(items);
        setAiNote(note);
        if (items.length === 0) {
          toast(t('quotes.ai.noNewItems'));
        } else {
          toast.success(t('quotes.ai.estimateReady', { count: items.length }));
        }
      },
      onError: (error) => {
        const errorCode = getApiErrorCode(error);
        if (errorCode) {
          const localized = t(errorCode);
          if (localized !== errorCode) {
            toast.error(localized);
            return;
          }
        }
        const backendMessage = getApiErrorMessage(error);
        toast.error(backendMessage || t('quotes.ai.estimateFailed'), { duration: 8000 });
      },
    });
  }

  function applyAiResults(items: AiEstimateItem[]) {
    const newLines: LineItem[] = items.map((item) => ({
      description: item.description,
      quantity: toSafeNumber(item.quantity),
      unitPrice: toSafeNumber(item.unit_price).toFixed(2),
      vatRate: String(item.vat_rate),
    }));
    // Replace empty lines or append
    const hasContent = lines.some((l) => l.description.trim() !== '' || l.unitPrice !== '');
    if (hasContent) {
      setLines([...lines, ...newLines]);
    } else {
      setLines(newLines);
    }
    // Apply AI note to quote notes if present
    if (aiNote) {
      setNotes((prev) => (prev ? prev + '\n\n' + aiNote : aiNote));
    }
    setShowAiPanel(false);
    setAiResults(null);
    setAiNote('');
    toast.success(t('quotes.ai.linesApplied', { count: items.length }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createQuote.mutate(
      { clientId, clientLanguage, issueDate, validUntil, notes, lines: lines.map((l) => ({ description: l.description, quantity: l.quantity, unitPrice: l.unitPrice, vatRate: l.vatRate })) },
      {
        onSuccess: (data) => {
          clearDraft();
          if (images.length > 0 && data?.id) {
            uploadImages.mutate(
              { quoteId: data.id, files: images },
              {
                onSuccess: () => {
                  toast.success(t('quotes.created'));
                  navigate(`/quotes/${data.id}`);
                },
                onError: () => {
                  toast.success(t('quotes.created'));
                  toast.error(t('quotes.imagesUploadFailed'));
                  navigate(`/quotes/${data.id}`);
                },
              }
            );
          } else {
            toast.success(t('quotes.created'));
            navigate(`/quotes/${data.id}`);
          }
        },
      }
    );
  }

  // Compute AI estimate totals for preview
  const aiTotals = aiResults
    ? aiResults.reduce((acc, item) => {
        const net = toSafeNumber(item.quantity) * toSafeNumber(item.unit_price);
        const vat = net * (item.vat_rate / 100);
        return { net: acc.net + net, vat: acc.vat + vat, gross: acc.gross + net + vat };
      }, { net: 0, vat: 0, gross: 0 })
    : null;

  if (quota.isLoaded && !quota.canCreateInvoice) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600 p-1">←</button>
          <h1 className="text-xl sm:text-2xl font-bold">{t('quotes.createQuote')}</h1>
        </div>
        <QuotaExceededBanner
          type="invoices"
          used={quota.invoicesUsed}
          limit={quota.invoicesLimit}
          planLabel={quota.planLabel}
          fullPage
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-700 p-1.5 -ml-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">{t('quotes.createQuote')}</h1>
        <div className="ml-auto">
          <button type="button" className="text-sm text-gray-500 hover:text-red-600 transition-colors px-3 py-1.5 rounded-lg hover:bg-red-50" onClick={handleResetAll}>
            {t('quotes.resetAll')}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="max-w-6xl">
          <div className="space-y-5 sm:space-y-6">
            {/* AI Estimation Panel */}
            <div className="card overflow-hidden rounded-xl shadow-sm">
              <button
                type="button"
                className="w-full card-header flex items-center justify-between bg-gradient-to-r from-violet-50 to-purple-50 hover:from-violet-100 hover:to-purple-100 transition-colors px-4 sm:px-6 py-3"
                onClick={() => setShowAiPanel(!showAiPanel)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">✨</span>
                  <h3 className="font-semibold text-violet-800 text-sm sm:text-base">{t('quotes.ai.title')}</h3>
                  <span className="text-xs bg-violet-200 text-violet-700 px-2 py-0.5 rounded-full font-medium">AI</span>
                </div>
                <svg className={`w-5 h-5 text-violet-600 transition-transform ${showAiPanel ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showAiPanel && (
                <div className="p-4 sm:p-6 space-y-4 border-t border-violet-100">
                  {!quota.canUseAiEstimate && (
                    <QuotaExceededBanner type="aiEstimate" planLabel={quota.planLabel} />
                  )}
                  {quota.canUseAiEstimate && (<>
                  <p className="text-sm text-gray-600">{t('quotes.ai.description')}</p>
                  <div className="relative">
                    <textarea
                      className={`input min-h-[100px] sm:min-h-[120px] pr-12 text-base sm:text-sm ${isListening ? 'ring-2 ring-red-400 border-red-400' : ''}`}
                      placeholder={t('quotes.ai.placeholder')}
                      value={aiDescription}
                      onChange={(e) => setAiDescription(e.target.value)}
                      maxLength={5000}
                    />
                    <button
                      type="button"
                      title={t(isListening ? 'quotes.ai.dictateStop' : 'quotes.ai.dictateStart')}
                      className={`absolute top-2 right-2 p-2.5 rounded-full transition-colors ${
                        isListening
                          ? 'bg-red-100 text-red-600 hover:bg-red-200 animate-pulse'
                          : 'bg-gray-100 text-gray-500 hover:bg-violet-100 hover:text-violet-600'
                      }`}
                      onClick={toggleDictation}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        {isListening ? (
                          <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
                        ) : (
                          <>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 1a4 4 0 0 1 4 4v6a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 10v1a7 7 0 0 1-14 0v-1" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 19v4m-4 0h8" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                  {isListening && (
                    <p className="text-xs text-red-500 flex items-center gap-1 animate-pulse">
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full" />
                      {t('quotes.ai.dictateListening')}
                    </p>
                  )}

                  {/* Show existing lines hint when lines exist */}
                  {lines.some((l) => l.description.trim() !== '') && (
                    <div className="flex items-start gap-2 bg-blue-50 rounded-lg px-3 py-2">
                      <span className="text-blue-500 mt-0.5 text-sm">ℹ️</span>
                      <p className="text-xs text-blue-700">{t('quotes.ai.existingLinesHint')}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-gray-400">{aiDescription.length}/5000</span>
                    <button
                      type="button"
                      className="btn bg-violet-600 hover:bg-violet-700 text-white px-4 sm:px-6 text-sm"
                      onClick={handleAiEstimate}
                      disabled={aiEstimate.isPending || !aiDescription.trim()}
                    >
                      {aiEstimate.isPending ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          {t('quotes.ai.estimating')}
                        </span>
                      ) : (
                        t('quotes.ai.generate')
                      )}
                    </button>
                  </div>

                  {/* AI Results Preview */}
                  {aiResults && (
                    <div className="border border-violet-200 rounded-lg overflow-hidden mt-4">
                      <div className="bg-violet-50 px-3 sm:px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <h4 className="font-semibold text-violet-800 text-sm">
                          {t('quotes.ai.results', { count: aiResults.length })}
                        </h4>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm bg-violet-600 hover:bg-violet-700 text-white flex-1 sm:flex-none"
                            onClick={() => applyAiResults(aiResults)}
                          >
                            {t('quotes.ai.applyAll')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary flex-1 sm:flex-none"
                            onClick={() => setAiResults(null)}
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </div>

                      {/* Mobile: card layout for AI results */}
                      <div className="sm:hidden divide-y divide-gray-100">
                        {aiResults.map((item, i) => {
                          const unitPrice = toSafeNumber(item.unit_price);
                          const net = toSafeNumber(item.quantity) * unitPrice;
                          const gross = net * (1 + item.vat_rate / 100);
                          return (
                            <div key={i} className="p-3 space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${CATEGORY_COLORS[item.category] || 'bg-gray-100 text-gray-800'}`}>
                                  {t(`quotes.ai.categories.${item.category}`, item.category)}
                                </span>
                                <span className="text-sm font-semibold text-gray-900">€{gross.toFixed(2)}</span>
                              </div>
                              <p className="text-sm text-gray-800">{item.description}</p>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span>{item.quantity} {item.unit}</span>
                                <span>× €{unitPrice.toFixed(2)}</span>
                                <span>{item.vat_rate}% {t('invoices.vat')}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Desktop: table layout for AI results */}
                      <div className="hidden sm:block overflow-x-auto">
                        <table className="table text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="text-xs">{t('quotes.ai.category')}</th>
                              <th className="text-xs">{t('invoices.description')}</th>
                              <th className="text-xs text-center">{t('invoices.quantity')}</th>
                              <th className="text-xs">{t('quotes.ai.unit')}</th>
                              <th className="text-xs text-right">{t('invoices.unitPrice')}</th>
                              <th className="text-xs text-center">{t('invoices.vat')}</th>
                              <th className="text-xs text-right">{t('common.amount')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {aiResults.map((item, i) => {
                              const unitPrice = toSafeNumber(item.unit_price);
                              const net = toSafeNumber(item.quantity) * unitPrice;
                              const gross = net * (1 + item.vat_rate / 100);
                              return (
                                <tr key={i} className="hover:bg-gray-50">
                                  <td>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[item.category] || 'bg-gray-100 text-gray-800'}`}>
                                      {t(`quotes.ai.categories.${item.category}`, item.category)}
                                    </span>
                                  </td>
                                  <td className="text-xs">{item.description}</td>
                                  <td className="text-center text-xs">{item.quantity}</td>
                                  <td className="text-xs">{item.unit}</td>
                                  <td className="text-right text-xs">€{unitPrice.toFixed(2)}</td>
                                  <td className="text-center text-xs">{item.vat_rate}%</td>
                                  <td className="text-right text-xs font-medium">€{gross.toFixed(2)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {aiNote && (
                        <div className="bg-amber-50 border-t border-amber-200 px-3 sm:px-4 py-3">
                          <div className="flex items-start gap-2">
                            <span className="text-amber-600 mt-0.5">📝</span>
                            <div>
                              <h5 className="text-xs font-semibold text-amber-800 mb-1">{t('quotes.ai.noteTitle')}</h5>
                              <p className="text-xs text-amber-900 leading-relaxed">{aiNote}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {aiTotals && (
                        <div className="bg-violet-50 px-3 sm:px-4 py-3 flex flex-wrap justify-end gap-3 sm:gap-6 text-sm border-t border-violet-200">
                          <span className="text-gray-600">{t('invoices.subtotal')}: <strong>€{aiTotals.net.toFixed(2)}</strong></span>
                          <span className="text-gray-600">{t('invoices.vat')}: <strong>€{aiTotals.vat.toFixed(2)}</strong></span>
                          <span className="text-violet-800 font-bold">{t('invoices.total')}: €{aiTotals.gross.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  </>)}
                </div>
              )}
            </div>

            <div className="card p-4 sm:card-body grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 rounded-xl shadow-sm">
              <div className="col-span-2 sm:col-span-1">
                <label className="label">{t('quotes.client')} *</label>
                <div className="flex gap-2">
                  <select className="input text-base sm:text-sm flex-1" value={clientId} onChange={(e) => setClientId(e.target.value)} required>
                    <option value="">{t('invoices.selectClient')}</option>
                    {clientsData?.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <button type="button" className="btn btn-secondary px-3" onClick={() => setShowClientModal(true)} title={t('clients.addClient')}>+</button>
                </div>
              </div>
              <div>
                <label className="label">{t('quotes.clientLanguage')} *</label>
                <select className="input text-base sm:text-sm" value={clientLanguage} onChange={(e) => setClientLanguage(e.target.value)}>
                  {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label">{t('quotes.date')} *</label>
                <input type="date" className="input text-base sm:text-sm" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} required />
              </div>
              <div>
                <label className="label">{t('quotes.validUntil')} *</label>
                <input type="date" className="input text-base sm:text-sm" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} required />
              </div>
            </div>

            <div className="card rounded-xl shadow-sm">
              <div className="card-header flex items-center justify-between px-5 sm:px-6 py-3.5 sm:py-4 border-b border-gray-100">
                <h3 className="font-semibold text-base text-gray-900">{t('invoices.lines')}</h3>
                <button type="button" className="btn btn-secondary btn-sm" onClick={addLine}>+ {t('common.add')}</button>
              </div>

              {/* Desktop: table layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="table text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-xs py-3 font-medium text-gray-700">{t('invoices.description')}</th>
                      <th className="w-20 text-xs py-3 font-medium text-gray-700 text-center">{t('invoices.quantity')}</th>
                      <th className="w-36 text-xs py-3 font-medium text-gray-700 text-right">{t('invoices.unitPrice')}</th>
                      <th className="w-28 text-xs py-3 font-medium text-gray-700">{t('invoices.vat')}</th>
                      <th className="text-right w-28 text-xs py-3 font-medium text-gray-700">{t('common.amount')}</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lines.map((line, i) => {
                      const lt = lineTotal(line);
                      return (
                        <tr key={i} className="hover:bg-gray-50 transition-colors">
                          <td className="py-2.5"><DescriptionAutocomplete value={line.description} onChange={(val) => updateLine(i, 'description', val)} placeholder={t('invoices.description')} required /></td>
                          <td className="py-2.5"><input type="number" min={1} step={1} className="input text-center text-sm py-1.5" value={line.quantity} onChange={(e) => updateLine(i, 'quantity', Number(e.target.value))} required /></td>
                          <td className="py-2.5">
                            <input
                              type="text"
                              inputMode="decimal"
                              className="input text-right tabular-nums text-sm py-1.5"
                              placeholder="0.00"
                              value={line.unitPrice}
                              onChange={(e) => handlePriceChange(i, e.target.value)}
                              onBlur={() => handlePriceBlur(i)}
                              required
                            />
                          </td>
                          <td className="py-2.5">
                            <select className="input min-w-[5rem] text-sm py-1.5" value={line.vatRate} onChange={(e) => updateLine(i, 'vatRate', e.target.value)}>
                              {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                            </select>
                          </td>
                          <td className="text-right font-semibold tabular-nums py-2.5 text-gray-900">€{lt.gross.toFixed(2)}</td>
                          <td className="py-2.5">
                            <button 
                              type="button" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors" 
                              onClick={() => removeLine(i)} 
                              disabled={lines.length <= 1}
                              title={t('common.delete')}
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile: card layout for lines */}
              <div className="sm:hidden divide-y divide-gray-100">
                {lines.map((line, i) => {
                  const lt = lineTotal(line);
                  return (
                    <div key={i} className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs font-medium text-gray-400">#{i + 1}</span>
                        <button 
                          type="button" 
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors" 
                          onClick={() => removeLine(i)} 
                          disabled={lines.length <= 1}
                          title={t('common.delete')}
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">{t('invoices.description')}</label>
                        <DescriptionAutocomplete value={line.description} onChange={(val) => updateLine(i, 'description', val)} className="input text-base" placeholder={t('invoices.description')} required />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">{t('invoices.quantity')}</label>
                          <input type="number" min={1} step={1} className="input text-base text-center" value={line.quantity} onChange={(e) => updateLine(i, 'quantity', Number(e.target.value))} required />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">{t('invoices.unitPrice')}</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="input text-base text-right tabular-nums"
                            placeholder="0.00"
                            value={line.unitPrice}
                            onChange={(e) => handlePriceChange(i, e.target.value)}
                            onBlur={() => handlePriceBlur(i)}
                            required
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">{t('invoices.vat')}</label>
                          <select className="input text-base" value={line.vatRate} onChange={(e) => updateLine(i, 'vatRate', e.target.value)}>
                            {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end pt-1">
                        <span className="text-sm font-semibold text-gray-900 tabular-nums">€{lt.gross.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })}
                <div className="p-4">
                  <button type="button" className="btn btn-secondary btn-sm w-full" onClick={addLine}>+ {t('common.add')}</button>
                </div>
              </div>

              {/* Totals Summary */}
              <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 space-y-3">
                <div className="flex justify-end gap-8 text-sm"><span className="text-gray-500 font-medium">{t('invoices.subtotal')}</span><span className="tabular-nums text-gray-900 text-right min-w-[100px]">€{totals.net.toFixed(2)}</span></div>
                <div className="flex justify-end gap-8 text-sm"><span className="text-gray-500 font-medium">{t('invoices.vat')}</span><span className="tabular-nums text-gray-900 text-right min-w-[100px]">€{totals.vat.toFixed(2)}</span></div>
                <hr className="border-gray-300" />
                <div className="flex justify-end gap-8 text-base font-bold"><span className="text-gray-900">{t('invoices.total')}</span><span className="tabular-nums text-gray-900 text-right min-w-[100px]">€{totals.gross.toFixed(2)}</span></div>
              </div>
            </div>

            <div className="card p-4 sm:card-body rounded-xl shadow-sm">
              <label className="label text-sm font-medium text-gray-900 mb-2">{t('common.notes')}</label>
              <textarea className="input min-h-[80px] text-base sm:text-sm" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('common.notes')} />
            </div>

            {/* Image Upload */}
            <div className="card rounded-xl shadow-sm">
              <div className="card-header flex items-center justify-between px-5 sm:px-6 py-3.5 sm:py-4 border-b border-gray-100">
                <h3 className="font-semibold text-base text-gray-900">{t('quotes.images')}</h3>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => imageInputRef.current?.click()}
                >
                  <svg className="w-4 h-4 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  {t('quotes.addImages')}
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageSelect}
                />
              </div>
              {imagePreviews.length > 0 && (
                <div className="p-4 sm:card-body">
                  <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                    {imagePreviews.map((src, i) => (
                      <div key={i} className="relative group rounded-lg overflow-hidden border border-gray-200 aspect-square">
                        <img src={src} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-80 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                          onClick={() => removeImage(i)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {imagePreviews.length === 0 && (
                <div
                  className="p-4 sm:card-body flex flex-col items-center justify-center py-8 text-gray-400 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => imageInputRef.current?.click()}
                >
                  <svg className="w-10 h-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                  </svg>
                  <span className="text-sm">{t('quotes.dropImages')}</span>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-2">
              <button type="submit" className="btn btn-primary px-8 py-2.5 font-medium shadow-md hover:shadow-lg transition-all" disabled={createQuote.isPending}>
                {createQuote.isPending ? t('common.saving') : t('quotes.createQuote')}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Quick-create client modal */}
      {showClientModal && (
        <div className="modal-backdrop">
          <form onSubmit={handleQuickCreateClient} className="card w-full max-w-lg modal-sheet">
            <div className="card-header"><h2 className="text-lg font-bold">{t('clients.addClient')}</h2></div>
            <div className="card-body space-y-4">
              {/* Client type toggle */}
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button type="button"
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${clientForm.type === 'individual' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => setClientForm({ ...clientForm, type: 'individual', vatNumber: '' })}>
                  {t('clients.types.individual')}
                </button>
                <button type="button"
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors border-l border-gray-200 ${clientForm.type === 'company' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => setClientForm({ ...clientForm, type: 'company' })}>
                  {t('clients.types.company')}
                </button>
              </div>

              {/* VAT + KBO lookup (company only) */}
              {clientForm.type === 'company' && (
                <div>
                  <label className="label">{t('clients.vatNumber')}</label>
                  <div className="flex gap-2">
                    <input type="text" className="input flex-1" value={clientForm.vatNumber} onChange={(e) => setClientForm({ ...clientForm, vatNumber: e.target.value })} placeholder="BE0123456789" />
                    <button type="button" className="btn btn-secondary text-xs whitespace-nowrap" disabled={!clientForm.vatNumber || clientForm.vatNumber.length < 10 || kboLoading}
                      onClick={() => setVatLookup(clientForm.vatNumber)}>
                      {kboLoading ? '...' : t('kbo.findCompany')}
                    </button>
                  </div>
                  {kboData && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                      <div className="font-medium text-green-800 mb-1">{kboData.nameFr || kboData.nameNl}</div>
                      <div className="text-green-700 text-xs space-y-0.5 mb-2">
                        {kboData.streetFr && <div>{kboData.streetFr} {kboData.houseNumber}, {kboData.zipcode} {kboData.municipalityFr}</div>}
                        {kboData.activities?.[0] && <div>{t('kbo.activity')}: {kboData.activities[0].descriptionFr || kboData.activities[0].naceCode}</div>}
                      </div>
                      <button type="button" className="btn btn-primary w-full text-sm py-1.5" onClick={() => applyKboData(kboData)}>
                        {t('kbo.useCompany')}
                      </button>
                    </div>
                  )}
                  {vatLookup && !kboLoading && !kboData && (
                    <p className="text-xs text-gray-400 mt-1">{t('kbo.notFound')}</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label">{t('clients.name')} *</label>
                  <input type="text" className="input" value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} required autoFocus />
                </div>
                <div>
                  <label className="label">{t('clients.email')}</label>
                  <input type="email" className="input" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} />
                </div>
                <div>
                  <label className="label">{t('clients.phone')}</label>
                  <input type="text" className="input" value={clientForm.phone} onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <label className="label">{t('clients.address')}</label>
                  <input type="text" className="input" value={clientForm.address} onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })} />
                </div>
                <div>
                  <label className="label">{t('clients.city')}</label>
                  <input type="text" className="input" value={clientForm.city} onChange={(e) => setClientForm({ ...clientForm, city: e.target.value })} />
                </div>
                <div>
                  <label className="label">{t('clients.postalCode')}</label>
                  <input type="text" className="input" value={clientForm.postalCode} onChange={(e) => setClientForm({ ...clientForm, postalCode: e.target.value })} />
                </div>
                <div>
                  <label className="label">{t('clients.country')}</label>
                  <input type="text" className="input" maxLength={2} value={clientForm.country} onChange={(e) => setClientForm({ ...clientForm, country: e.target.value.toUpperCase() })} />
                </div>
              </div>
            </div>
            <div className="card-body border-t flex gap-2 justify-end">
              <button type="button" className="btn btn-secondary" onClick={() => { setShowClientModal(false); setClientForm(emptyClientForm); setVatLookup(''); }}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" disabled={createClient.isPending}>{t('common.save')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
